import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { heureka } from "@otto-ec/heureka";
import type { Appendable } from "../util/Appendable";
import { clearFormInput, interceptSubmit } from "../filter/FilterFormActions";
import {
  dereference,
  extractFeatureWithSpecificLabels,
  getStatus,
  listFeatures,
  readLabel,
  readLegacyLabel,
  submitActionTrackingFromWithSpecificLabels,
  updateLegacyLabel,
  updateStatus,
} from "../tracking/FeatureTracking";
import {
  isNba,
  isNotSelected,
  isSelected,
  setFeatureStatus,
  SvelteFacetValue,
  updateFacetValueTagPosition,
  updateNBAPosition,
  updateNBARow,
  updateSanTopFacetValueWithNba,
} from "../filter/SvelteFacetValue";
import { arrayToMapWithIndexValues, clone, element, getElementById, notEmpty } from "../util/Utils";
import { isMobile } from "../util/Breakpoint";
import { events } from "../events";
import tracker from "../tracking/Tracker";
import { setChangeLabels } from "../filter/FilterTracking";
import { trackInAllEvents } from "../tracking/GlobalTracking";
import type { ActionName, FeatureStatus } from "../tracking/Tracking";
import { SvelteFacet } from "../filter/SvelteFacet";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import { RefinementBar } from "../refinementBar/RefinementBar";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { filterSheetButton, ocSheetContent, quickFilterButton, refinementBar } from "../filtersheet/Selectors";
import { syncRefinementBarCategoryPath } from "../categorypath/categoryPath";
import { all } from "../util/Reductions";
import { getActiveVariation } from "../experiment/Experiments";

const CLASS_NEXT_BEST_ACTION_WRAPPER = "hc_nextBestAction";
const CLASS_NEXT_BEST_ACTION_LOADING = `${CLASS_NEXT_BEST_ACTION_WRAPPER}--loading`;
const CLASS_VALUE_GROUP = "hc_facetValueGroup";
const CLASS_VALUE_GROUP_DENSE_MODE = `${CLASS_VALUE_GROUP}__list--denseMode`;
const SELECTOR_NBA_CONTAINER = `.${CLASS_VALUE_GROUP}__container`;
const SELECTOR_SHOW_MORE = `.${CLASS_VALUE_GROUP}--showMore`;
const SELECTOR_SHOW_LESS = `.${CLASS_VALUE_GROUP}--showLess`;
const CLASS_DESELECT_ALL = "hc_deselectAll";
const SELECTOR_DESELECT_ALL = `.${CLASS_DESELECT_ALL}`;
const SELECTOR_TEMPLATE_FACET_VALUE_TAGS = `.hc_svelteNextBestActionWrapper .${CLASS_NEXT_BEST_ACTION_WRAPPER}`;
const VISIBILITY_THRESHOLD = 0.75;
const SCROLL_STOP_DELAY_MS = 1000; /*                                                     */

export class NextBestActionComponent implements Appendable {
  static readonly factory = HeurekaElementFactory.byClass(CLASS_NEXT_BEST_ACTION_WRAPPER, NextBestActionComponent);
  private scrollObserver: IntersectionObserver | null = null;
  private scrollHandler: (() => void) | null = null;
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;

  /*               */

  constructor(readonly tags: HTMLElement) {}

  /*                  */

  static template(rootElement?: ParentNode | null) {
    return NextBestActionComponent.factory.pick(SELECTOR_TEMPLATE_FACET_VALUE_TAGS, rootElement);
  }

  static fromRefinementBar(): NextBestActionComponent | undefined {
    const refBar = refinementBar();
    return refBar ? NextBestActionComponent.factory.pick(undefined, refBar) : undefined;
  }

  /*               */

  init() {
    syncRefinementBarCategoryPath(this.values);
    this.registerListener();
    this.deselectAllButton?.addEventListener("click", () => {
      /*                                                                                         */
      this.selectedValues.forEach((tag) => {
        tag.updateTrackingLabelsOnReferenced("deleted", "x_all_top", "list_initial");
      });
    });
    this.selectedValues.forEach((tag) => {
      tag.facetValue.addEventListener("click", () => {
        /*                                                                                   */
        tag.updateTrackingLabelsOnReferenced("deleted", "x_single_top", "list_initial");
      });
    });
    return this;
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    NextBestActionComponent.emitSelectedFacetValues(event, rootElement);
  }

  private static emitSelectedFacetValues(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    const refinementBar = RefinementBar.facetValueTagsElement(rootElement);
    if (refinementBar) {
      const nba = NextBestActionComponent.factory.pick(undefined, refinementBar);
      const selectedValues = nba?.selectedValues.map((value) => value.title);
      if (selectedValues && selectedValues.length) {
        events.facetValueTagsLoaded(event.rule, selectedValues);
      }
    }
  }

  /*                       */

  clone() {
    return NextBestActionComponent.factory.create(clone(this.tags));
  }

  get rule() {
    return this.tags.dataset.rule || "none";
  }

  get deselectAllButton(): HTMLElement | null {
    return element(SELECTOR_DESELECT_ALL, this.tags);
  }

  get topQuickFacet(): string | undefined {
    return this.tags.dataset.tsTopQuickFacet;
  }

  get values(): SvelteFacetValue[] {
    return SvelteFacetValue.factory.all(this.tags);
  }

  get expandedValues(): SvelteFacetValue[] {
    return this.values.filter((value) => value.expanded);
  }

  get selectedValues(): SvelteFacetValue[] {
    return this.values.filter(isSelected);
  }

  get deselectAll(): HTMLFormElement | undefined {
    return this.tags.querySelector(SELECTOR_DESELECT_ALL);
  }

  get ulElement(): HTMLUListElement | null {
    return this.tags.querySelector("ul");
  }

  get nbaContainerWidth(): number {
    return this.tags.querySelector(SELECTOR_NBA_CONTAINER)?.clientWidth || 0;
  }

  get showMore(): HTMLElement | null {
    return this.tags.querySelector(SELECTOR_SHOW_MORE);
  }

  get showMoreWidth(): number {
    return this.showMore?.clientWidth || 0;
  }

  get showLess(): HTMLElement | null {
    return this.tags.querySelector(SELECTOR_SHOW_LESS);
  }

  show() {
    this.tags.classList.remove(CLASS_NEXT_BEST_ACTION_LOADING);
  }

  private addShowMoreListener() {
    this.showMore?.addEventListener("click", () => {
      const san_FilterWhatIf = readLegacyLabel(filterSheetButton(), "san_FilterWhatIf");
      const labels: Partial<TrackingLabels> = { san_Foldout: "show_more_nbf", san_FilterWhatIf };
      this.values.forEach((facetValue) => {
        facetValue.hidden = false;
        setFeatureStatus(facetValue, "visible");
      });
      const features = this.getFeaturesFor(this.expandedValues);
      const actionName: ActionName = "open";
      const action = {
        name: actionName,
        features: features,
      };
      this.showMore.hidden = true;
      if (this.showLess) {
        this.showLess.hidden = false;
      }
      this.ulElement?.classList.remove(CLASS_VALUE_GROUP_DENSE_MODE);
      tracker.submitAction(labels, action);
    });
  }

  private getFeaturesFor<T extends keyof TrackingLabels>(values: SvelteFacetValue[], keepLabels?: T[]) {
    const features = values
      .map((value) => {
        if (value.referencedElement) {
          return extractFeatureWithSpecificLabels(value.referencedElement, keepLabels);
        }
        return undefined;
      })
      .filter(notEmpty);
    const filterIds = extractUniqueIdsDeprecated(values, (svelteFacet) => svelteFacet.filter.form.id);
    const facetIds = extractUniqueIdsDeprecated(values, (svelteFacet) => svelteFacet.facet.id);
    return [
      ...listFeatures(["Filter-Overview"]),
      ...listFeatures(["Filter"], undefined, filterIds.join(",")),
      ...listFeatures(["Facet"], undefined, facetIds.join(",")),
      ...features,
    ];
  }

  private addShowLessListener() {
    this.showLess?.addEventListener("click", () => {
      this.denseMode();
      this.showLess.hidden = true;
      this.ulElement?.classList.add(CLASS_VALUE_GROUP_DENSE_MODE);
      const san_FilterWhatIf = readLegacyLabel(filterSheetButton(), "san_FilterWhatIf");
      const trackingData: Partial<TrackingLabels> = { san_Foldout: "show_less_nbf", san_FilterWhatIf };
      tracker.submitEvent(trackingData);
    });
  }

  initializeExpander() {
    const anyHidden = this.denseMode();
    this.show();
    if (anyHidden) {
      this.addShowMoreListener();
      this.addShowLessListener();
    }
    return this;
  }

  private denseMode() {
    this.addNbaRowLabel();
    const denseModeValues = this.denseModeVisibilities();
    const anyHidden = denseModeValues.some((value) => value.hidden === true);
    if (anyHidden) {
      this.showMoreVisibility(anyHidden);
      this.adjustExpander(denseModeValues);
    }
    return anyHidden;
  }

  private showMoreVisibility(isDenseMode: boolean) {
    this.ulElement.classList.toggle(CLASS_VALUE_GROUP_DENSE_MODE, isDenseMode);
    if (this.showMore) {
      this.showMore.hidden = !isDenseMode;
    }
  }

  private denseModeVisibilities() {
    const firstValue = (this.values.length > 0 && this.values[0]) || undefined;
    return this.values.map((facetValue) => {
      if (facetValue.offsetTop > firstValue.offsetTop) {
        facetValue.hidden = true;
        facetValue.expanded = true;
        setFeatureStatus(facetValue, "loaded");
      } else {
        setFeatureStatus(facetValue, "visible");
      }
      return facetValue;
    });
  }

  private adjustExpander(values: SvelteFacetValue[]) {
    const gap = parseFloat(getComputedStyle(this.ulElement).gap);
    const visibleValues = values.filter((value) => value.hidden === false).reverse();
    const containerWidth = this.nbaContainerWidth;
    const valuesWidth = visibleValues
      .map((value) => {
        return (value.liElem?.clientWidth || 0) + gap;
      })
      .reduce((a, b) => a + b, gap);
    let availableWidth = containerWidth - valuesWidth;
    visibleValues.map((value) => {
      if (availableWidth < this.showMoreWidth + gap) {
        availableWidth = availableWidth + (value.liElem?.clientWidth || 0) + gap;
        value.hidden = true;
        value.expanded = true;
        setFeatureStatus(value, "loaded");
      }
    });
  }

  appendTo(elem: HTMLElement): void {
    elem.appendChild(this.tags);
  }

  removeNonSelectedNbaWithRef(ref: string): void {
    const value = this.values.find((value) => value.ref === ref && !value.checked);
    value?.liElem?.remove();
  }

  static couldBeVisible(): boolean {
    return true;
  }

  static isNotVisible(): boolean {
    return !NextBestActionComponent.couldBeVisible();
  }

  static hideBanner(): boolean {
    const facetValueTagsTemplate = NextBestActionComponent.fromRefinementBar();
    return facetValueTagsTemplate && facetValueTagsTemplate.values.length > 0;
  }

  /*                 */

  private handleChange(event: Event) {
    if (event.target instanceof HTMLElement) {
      const referenced = dereference(event.target);
      const filterForm = referenced.closest("form");
      /*                                                                                           */
      /*                                                   */
      if (filterForm) {
        setChangeLabels(filterForm, { san_FacetLocation: "list_initial" });
        filterForm.dataset.tsFeatureFilterMethod = readLabel(referenced, "san_FilterMethod");
      }
    }

    heureka.filterSection_change.emit({ target: event.target });
  }

  registerListener() {
    this.tags.addEventListener("change", this.handleChange);
    this.deselectAll?.addEventListener("submit", (event) => {
      this.values.forEach((value) => {
        const referenced = dereference(value.facetValue);
        clearFormInput(referenced);
      });
      interceptSubmit(event);
    });
  }

  /*       */

  /*                                                                       */
  public trackInitialVisibilityNba(): string[] {
    const refs = this.tags?.querySelectorAll<HTMLElement>("[data-ts-feature-ref]");
    return Array.from<HTMLElement>(refs ?? [])
      .map((value: HTMLElement) => {
        const isInitiallyVisible = isElementVisible(value, this.tags);
        this.updateReferencedStatus(value, isInitiallyVisible);
        return value.dataset.tsFeatureRef;
      })
      .filter(notEmpty);
  }

  private updateReferencedStatus(value: HTMLElement, isInitiallyVisible: boolean) {
    const origin = this.getOrigin(value);
    if (origin) {
      const originStatus = getStatus(origin);
      if (!originStatus || originStatus === "visible" || originStatus === "loaded") {
        updateStatus(origin, isInitiallyVisible ? "visible" : "loaded");
      }
    }
  }

  private addNbaRowLabel() {
    const offsets = this.values.map((li) => li.offsetTop);
    const uniqueOffsets = Array.from(new Set(offsets)).sort();
    const offsetsMap: Map<number, number> = arrayToMapWithIndexValues(uniqueOffsets, 1);
    this.values.forEach((value) => updateNBARow(value, offsetsMap.get(value.offsetTop)));
  }

  trackNbaRow() {
    const features = this.getFeaturesFor(this.values, ["san_FacetValue", "san_NBAPosition", "san_NBARow"]);
    tracker.addFeaturesToPageImpression(features);
  }

  isAllVisible = (ids: string[]) =>
    ids
      .map((id) => {
        const feature = getElementById(id);
        return feature ? getStatus(feature) === "visible" : false;
      })
      .reduce(all, true);

  addScrollTracking() {
    this.scrollObserver?.disconnect();
    const nbaScrollContainer = this.tags?.querySelector(".hc_nextBestAction__values") as HTMLElement;
    if (this.scrollHandler) {
      nbaScrollContainer?.removeEventListener("scroll", this.scrollHandler);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    if (!nbaScrollContainer) return;
    const values = nbaScrollContainer?.querySelectorAll<HTMLElement>("[data-ts-feature-ref]") || [];
    const valuesIds = Array.from(values)
      .map((v) => v.dataset?.tsFeatureRef)
      .filter(notEmpty);
    const nbaFacetValues: SvelteFacetValue[] = SvelteFacetValue.factory
      .pickAll(undefined, nbaScrollContainer)
      .filter(isNotSelected);
    const options = { root: nbaScrollContainer, threshold: VISIBILITY_THRESHOLD };
    const observer = new IntersectionObserver((entries) => {
      let observed = false;
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry?.target as HTMLDivElement;
          const origin = this.getOrigin(target);
          if (origin) {
            const originStatus = getStatus(origin);
            if (originStatus === "loaded") {
              updateStatus(origin, "visible");
              observed = true;
            }
          }
        }
      });
      if (this.isAllVisible(valuesIds)) {
        observer.disconnect();
      }

      if (observed) {
        const filterIds = extractUniqueIds(nbaFacetValues, (svelteFacet) => svelteFacet?.filter?.form?.id)
          .filter(notEmpty)
          .filter((filterId) => !valuesIds.includes(filterId));
        const facetIds = extractUniqueIds(nbaFacetValues, (svelteFacet) => svelteFacet?.facet?.id).filter(notEmpty);
        /*                                                                                                                                                         */
        submitActionTrackingFromWithSpecificLabels(
          nbaScrollContainer,
          "scroll",
          {},
          [
            "san_FacetValue",
            "san_NBAPosition",
            "san_TopFacetValue",
            "san_FilterName",
            "san_FilterFoldoutStatus",
            "san_FilterVisibility",
          ],
          [
            ...listFeatures(["Filter-Overview"]),
            ...(filterIds.length > 0
              ? listFeatures(["Filter"], undefined, filterIds.map((id) => `[id="${id}"]`).join(","))
              : []),
            ...(facetIds.length > 0
              ? listFeatures(["Facet"], undefined, facetIds.map((id) => `[id="${id}"]`).join(","))
              : []),
          ],
        );
      }
    }, options);
    const observe = () => values.forEach((v) => observer.observe(v));

    this.scrollHandler = () => {
      if (this.isAllVisible(valuesIds)) return;
      observer.disconnect();
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        observe();
        this.scrollTimeout = null;
      }, SCROLL_STOP_DELAY_MS);
    };

    nbaScrollContainer.addEventListener("scroll", this.scrollHandler);
    this.scrollObserver = observer;
  }

  private getOrigin(elem: HTMLElement) {
    const ref = elem?.dataset?.tsFeatureRef;
    return getElementById(ref);
  }
}

export function trackNba() {
  const nextBestActionComponent = NextBestActionComponent.fromRefinementBar();
  if (nextBestActionComponent) {
    nextBestActionComponent.values.filter(isSelected).forEach(updateFacetValueTagPosition);
    nextBestActionComponent.values.filter(isNba).forEach((value, index) => {
      updateNBAPosition(value, index);
      /*                                                                                                          */
      updateSanTopFacetValueWithNba(value);
    });

    const { tsLabels } = nextBestActionComponent.tags.dataset || {};
    if (tsLabels) {
      tracker.addToPageImpression(JSON.parse(tsLabels));
    }
  }
}

const visibleTagsDuringScroll: Set<string> = new Set();

/**
 *
 */
export function resetNBAScrollTracking(): void {
  visibleTagsDuringScroll.clear();
}

function extractUniqueIdsDeprecated(
  nbaValues: SvelteFacetValue[],
  extractor: (svelteFacet: ReturnType<typeof SvelteFacet.factory.closest>) => string | undefined,
): string[] {
  const ids = nbaValues
    .map((value) => SvelteFacet.factory.closest(value.referencedElement))
    .map(extractor)
    .filter(Boolean) as string[];

  return Array.from(new Set(ids)).map((id) => `[id="${id}"]`);
}

function extractUniqueIds(
  nbaValues: SvelteFacetValue[],
  extractor: (svelteFacet: ReturnType<typeof SvelteFacet.factory.closest>) => string | undefined,
): string[] {
  const ids = nbaValues
    .map((value) => SvelteFacet.factory.closest(value.referencedElement))
    .map(extractor)
    .filter(Boolean) as string[];

  return Array.from(new Set(ids));
}

function isElementVisible(
  element: HTMLElement | Element | null,
  containerRectOrElement: DOMRect | HTMLElement,
): boolean {
  if (!element) return false;

  const containerRect =
    containerRectOrElement instanceof DOMRect ? containerRectOrElement : containerRectOrElement.getBoundingClientRect();

  return calculateVisiblePercentage(element.getBoundingClientRect(), containerRect) >= VISIBILITY_THRESHOLD;
}

function calculateVisiblePercentage(itemRect: DOMRect, containerRect: DOMRect): number {
  const itemLeft = Math.max(itemRect.left, containerRect.left);
  const itemRight = Math.min(itemRect.right, containerRect.right);
  const visibleWidth = Math.max(0, itemRight - itemLeft);
  const totalWidth = itemRect.width;

  return totalWidth > 0 ? visibleWidth / totalWidth : 0;
}

/*                                                                                                        */
function trackNbaTopQuickFacet() {
  const nextBestActionComponent = NextBestActionComponent.fromRefinementBar();
  const topQuickFacet = nextBestActionComponent?.topQuickFacet;
  if (topQuickFacet) {
    /*                                                                                         */
    updateSanTopQuickFacet(ocSheetContent(), topQuickFacet);
    trackInAllEvents("san_TopQuickFacet", topQuickFacet);
  }
}

export function registerNextBestAction() {
  if (isMobile()) {
    if (getActiveVariation("e3911", "StatusQuo") === "Filterbar") {
      heureka.nextBestAction_loaded.subscribe(() => {
        const nextBestActionComponent = NextBestActionComponent.fromRefinementBar();
        const selectedValues = nextBestActionComponent?.selectedValues.map((value) => value.title);
        if (selectedValues && selectedValues.length) {
          events.facetValueTagsLoaded(nextBestActionComponent.rule, selectedValues);
        }
        nextBestActionComponent?.addScrollTracking();
        trackNba();
        nextBestActionComponent?.trackInitialVisibilityNba();
        trackNbaTopQuickFacet();
        heureka.nextBestAction_initialized.emit();
      });
      events.onFilterSheetOpen(trackNba);
    } else {
      events.onFilterSectionLoaded(() => {
        const nextBestActionComponent = NextBestActionComponent.fromRefinementBar();
        const selectedValues = nextBestActionComponent?.selectedValues.map((value) => value.title);
        if (selectedValues && selectedValues.length) {
          events.facetValueTagsLoaded(nextBestActionComponent.rule, selectedValues);
        }
        nextBestActionComponent?.addScrollTracking();
      });
      heureka.nextBestAction_loaded.subscribe(() => {
        trackNba();
        NextBestActionComponent.fromRefinementBar()?.trackInitialVisibilityNba();
        heureka.nextBestAction_initialized.emit();
      });
      /*                                          */
      /*                                                                                                      */
      /*                                          */
      events.onFilterSectionLoaded(trackNbaTopQuickFacet);
      events.onFilterSheetOpen(trackNba);
    }
  } else {
    events.onFilterSectionLoaded(() => {
      const nextBestActionComponent = NextBestActionComponent.fromRefinementBar();
      const selectedValues = nextBestActionComponent?.selectedValues.map((value) => value.title);
      if (selectedValues && selectedValues.length) {
        events.facetValueTagsLoaded(nextBestActionComponent.rule, selectedValues);
      }
    });
    heureka.nextBestAction_loaded.subscribe(() => {
      trackNba();
      NextBestActionComponent.fromRefinementBar()?.trackInitialVisibilityNba();
      heureka.nextBestAction_initialized.emit();
    });
  }
}

export function registerNextBestActionComponentHandlers() {
  if (isMobile()) {
    events.onFilterSectionLoaded(trackNba);
    /*                                          */
    /*                                                                                                      */
    /*                                          */
    events.onFilterSheetOpen(trackNba);
    events.onFilterSectionLoaded(trackNbaTopQuickFacet);
  } else {
    events.onFilterSectionLoaded(trackNba);
    events.onFilterSectionLoaded(trackNbaTopQuickFacet);
    /*                                                                                     */
    /*                                                                       */
    events.onRefinementBarLoaded(() => {
      const refBar = refinementBar();
      if (refBar) {
        NextBestActionComponent.factory.pick(undefined, refBar)?.initializeExpander().trackNbaRow();
      }
    });
  }
}

export function updateNbaValuesStatusTo(status: FeatureStatus) {
  const facetValueTagsTemplate = NextBestActionComponent.fromRefinementBar();
  if (facetValueTagsTemplate) {
    facetValueTagsTemplate.values.filter(isNba).forEach((value) => {
      setFeatureStatus(value, status);
    });
  }
}

export function updateSanTopQuickFacet(
  element: HTMLElement | null,
  san_TopQuickFacet: TrackingLabels["san_TopQuickFacet"],
) {
  if (element) {
    updateLegacyLabel(element, "san_TopQuickFacet", san_TopQuickFacet);
  }
}

export function readSanTopQuickFacet(): TrackingLabels["san_TopQuickFacet"] | undefined {
  const filterButton = quickFilterButton();
  return filterButton ? readLegacyLabel(filterButton, "san_TopQuickFacet") : undefined;
}
